<!-- 
  Contact List Item Component
  ---------------------------
  This component represents a single contact item within the Add Contact modal. It provides UI elements to display contact 
  information, validate email status, and allow users to select a primary contact.

  Features:
  - Displays contact information with validation for email addresses.
  - Checkbox to toggle the inclusion of the contact in the notify list.
  - Icon to mark the contact as the primary contact.
  - Warning icon for invalid email addresses.

  Component Interactions:
  - Users can toggle the contact's inclusion in the notify list via the checkbox.
  - Clicking the star icon sets the contact as the primary contact.
  - Invalid email addresses are indicated with a warning icon.
-->

<ion-item *ngIf="contact">
  <!-- Checkbox only for a contact that holds an address. A PO value that is a name
       (GP free text) can't be emailed and nothing is guessed from it, so there is
       no recipient to toggle — the user searches for the person instead. An
       off-domain vendor address IS checkable. -->
  <ion-checkbox
    *ngIf="canEmail"
    slot="start"
    [(ngModel)]="emailStatus"
    (ionChange)="onEmailStatusChange()">
  </ion-checkbox>

  <!-- Template for contacts with no usable address -->
  <ng-container *ngIf="!canEmail">
    <ng-container *ngTemplateOutlet="emailErrorTemplate"></ng-container>
  </ng-container>

  <!-- Display contact information + BambooHR insight badge (Employee / Group /
       External), with a visible job title · department · location line for a
       matched employee (the service-vs-project hint). -->
  <ion-label class="ion-text-wrap">
    <span class="contact-email">{{ contact.contact }}</span>
    <span *ngIf="badge"
          class="contact-insight-badge"
          [class.contact-insight-employee]="badge.kind === 'employee'"
          [class.contact-insight-group]="badge.kind === 'internal-group'"
          [class.contact-insight-external]="badge.kind === 'external'"
          [title]="badge.detail">
      {{ badge.label }}
    </span>
    <p *ngIf="badge?.meta" class="contact-insight-meta">{{ badge?.meta }}</p>
  </ion-label>
  
  <!-- Seeds the employee search with this name — the way to turn a name-only PO
       contact into a real recipient. The search also offers a first.last address
       built from the name (see typedEmailToAdd), labeled as a guess. -->
  <ion-button *ngIf="!canEmail" slot="end" size="small" fill="outline"
              (click)="onFindInDirectory()">
    <ion-icon name="search-outline" slot="start"></ion-icon>
    Find
  </ion-button>

  <!-- Icon to mark the contact as primary. Hidden for a contact with no address:
       the main contact goes in the log's Email field AND prints on the labels, so
       a name can't hold that role. -->
  <ion-icon
    *ngIf="canEmail"
    color="danger"
    slot="end"
    [title]="'Select Main Contact'"
    [name]="contact.contact === primaryContact ? 'star' : 'star-outline'"
    (click)="setAsPrimary()">
  </ion-icon>
</ion-item>

<!-- Template for a contact the PO gave without an address -->
<ng-template #emailErrorTemplate>
  <ion-icon
    color="warning"
    title="The PO gave a name, not an email address. Use Find to search the employee list for this person, or to add an address built from the name."
    name="warning">
  </ion-icon>
</ng-template>